import React, { Suspense } from 'react';
import {
  topicTreeFPTIs,
  communityFPTIs,
  extendedFooterFPTIs,
  searchBarFPTIs,
} from '../../jsV2/helpers/analytics/homePageAnalytics';
import ErrorBoundary from '../../jsV2/shared/ErrorBoundary';
import { pageNames } from '../../jsV2/common/constants';
import { isCommunityEnabled, isLoggedIn } from '../../jsV2/helpers/twHelper';
import { layoutSize, isBNAComponentEnabled, isQualtricsFeedbackEnabled } from '../../jsV2/helpers/globalHelper';
import { isShowOnlySearchLayoutEnabled, isBotExperienceEnabled } from '../helper';
import { trackImpression } from '../../jsV2/helpers/analytics/fptiHelper';
import { pageNames as fptiPage } from '../../js/lib/page-name-identifiers';

// Lazy loading components
const HeroPanel = React.lazy(() =>
  import(/* webpackChunkName: "HeroPanel.sh.chunk" */ '../../jsV2/home/containers/panels/HeroPanel'),
);
const TopicTreePanel = React.lazy(() =>
  import(/* webpackChunkName: "TopicTreePanel.sh.chunk" */ '../../jsV2/home/containers/panels/TopicTreePanel'),
);
const ExperienceNavBar = React.lazy(() =>
  import(/* webpackChunkName: "ExperienceNavBar.sh.chunk" */ '../../jsV2/components/experienceNavBar/ExperienceNavBar'),
);
const RecommendationsPanel = React.lazy(() =>
  import(
    /* webpackChunkName: "RecommendationsPanel.sh.chunk" */ '../../jsV2/home/containers/panels/RecommendationsPanel'
  ),
);
const CommunityPanel = React.lazy(() =>
  import(/* webpackChunkName: "CommunityPanel.sh.chunk" */ '../../jsV2/home/containers/panels/CommunityPanel'),
);
const ExtendedFooterPanel = React.lazy(() =>
  import(/* webpackChunkName: "ExtendedFooter.sh.chunk" */ '../../jsV2/home/containers/panels/ExtendedFooterPanel'),
);
const BNAPanel = React.lazy(() =>
  import(/* webpackChunkName: "BNAPanel.sh.chunk" */ '../../jsV2/home/containers/panels/BNAPanel'),
);
const QualtricsFeedbackPanel = React.lazy(() =>
  import(/* webpackChunkName: "QualtricsPanel.sh.chunk" */ '../../jsV2/home/containers/panels/QualtricsPanel'),
);

const SearchOnlyHomePage = () => {
  // New template layout using experimentation.
  const defaultLayout = () => (
    <div className="sh_home__container" data-testid="sh-home-container">
      {/* Display loader (Spinner) in center while loading */}
      <Suspense fallback={<div className="home-loader-container" />}>
        <ErrorBoundary>
          <HeroPanel FPTIData={searchBarFPTIs} size={layoutSize()} />
        </ErrorBoundary>

        <ErrorBoundary errorFPTI={topicTreeFPTIs.topicTreeError}>
          <TopicTreePanel pageName={pageNames.HOME} FPTIData={topicTreeFPTIs} />
        </ErrorBoundary>

        <ErrorBoundary>{isBNAComponentEnabled() ? <BNAPanel /> : null}</ErrorBoundary>

        <ErrorBoundary>{!isLoggedIn() ? <ExperienceNavBar /> : null}</ErrorBoundary>

        <ErrorBoundary>
          <RecommendationsPanel />
        </ErrorBoundary>

        <ErrorBoundary errorFPTI={communityFPTIs.communityModuleError}>
          {isCommunityEnabled() ? <CommunityPanel pageName={pageNames.HOME} FPTIData={communityFPTIs} /> : null}
        </ErrorBoundary>

        <ErrorBoundary errorFPTI={extendedFooterFPTIs.extendedFooterError}>
          <ExtendedFooterPanel pageName={pageNames.HOME} FPTIData={extendedFooterFPTIs} />
        </ErrorBoundary>

        <ErrorBoundary>
          {isQualtricsFeedbackEnabled(pageNames.HOME) ? <QualtricsFeedbackPanel pageName={pageNames.HOME} /> : null}
        </ErrorBoundary>
      </Suspense>
    </div>
  );

  // Innovation Guild: search only experiment FPTI data.
  const searchOnlyFPTI = () => {
    let eventNameExp = '';
    if (isShowOnlySearchLayoutEnabled()) {
      eventNameExp = 'search-only-faq-exp';
    } else if (isBotExperienceEnabled()) {
      eventNameExp = 'search-only-bot-exp';
    }

    const searchOnlyFpti = {
      id: 'homePage',
      link: `${eventNameExp}|home-page-opened`,
      event_name: eventNameExp,
      page: fptiPage.homePage.pageName,
      pgrp: fptiPage.homePage.pageGroupName,
      e: 'im',
    };
    trackImpression(searchOnlyFpti);
  };

  // Innovation Guild: search only experiment FPTI data.
  const renderOnlySearchLayout = () => {
    searchOnlyFPTI();
    return (
      <div className="sh_home__container" data-testid="sh-home-container">
        {/* Display loader (Spinner) in center while loading */}
        <Suspense fallback={<div className="home-loader-container" />}>
          <ErrorBoundary>
            <HeroPanel FPTIData={searchBarFPTIs} size={layoutSize()} />
          </ErrorBoundary>
          <ErrorBoundary>
            {isQualtricsFeedbackEnabled(pageNames.HOME) ? <QualtricsFeedbackPanel pageName={pageNames.HOME} /> : null}
          </ErrorBoundary>
        </Suspense>
      </div>
    );
  };

  // Render new home layout based on the experimentation.
  const renderLayout = () => {
    if (isShowOnlySearchLayoutEnabled() || isBotExperienceEnabled()) {
      return renderOnlySearchLayout();
    }
    return defaultLayout();
  };

  return renderLayout();
};

export default SearchOnlyHomePage;
